// src/components/FormLists/formListsGridConfig.js

/**
 * formListsGridConfig
 * -----------------------------------------------------------------------------
 * Form Lists-specific spreadsheet configuration.
 *
 * PURPOSE
 *  • Keep the shared spreadsheet engine domain-free
 *  • Define the required Form Lists columns in one place:
 *      - Option
 *      - Hint
 *      - Dependency
 *  • Keep Form Lists aligned with the existing form-local dropdown schema:
 *      dropdownOptions[optionsSetUFI] = [
 *        { optionId, label, value, hint, dep, order, systemValue }
 *      ]
 *  • Keep dependency editing as a custom Form Lists cell, not a generic
 *    spreadsheet text column
 *
 * IMPORTANT
 *  • This file does NOT render group headers, column headers, rows, or cells.
 *    The shared Spreadsheet/ files own rendering and behavior.
 *  • This file does NOT read/write React context.
 *  • This file does NOT import Firebase.
 *  • This file does NOT save dropdown options.
 *  • This file does NOT save dependencies.
 *  • This file does NOT know about SmartLists managed option sets.
 *
 * ENTERPRISE SPREADSHEET RULES
 *  • "Option" is the business-required meaningful column for Form Lists.
 *  • "Hint" is editable, but a hint-only row should not persist as an option.
 *  • "Dependency" is a custom read-only cell.
 *  • Paste targets should include Option and Hint only.
 *  • Dependency cells should open the existing Form Lists dependency workflow.
 * -----------------------------------------------------------------------------
 */

import {
  DEFAULT_ROW_APPEND_COUNT,
  DEFAULT_VISIBLE_ROW_COUNT,
  buildDefaultColumnWidths,
} from '../Spreadsheet/groupedSpreadsheetUtils';

export const FORM_LISTS_GROUP_ID_KEY = 'groupId';
export const FORM_LISTS_ROW_ID_KEY = 'rowId';
export const FORM_LISTS_ROW_KEY = 'optionRows';

/**
 * Form Lists visible row baseline
 * -----------------------------------------------------------------------------
 * Form Lists needs a taller default working area than the generic shared
 * Spreadsheet engine because dropdown option sets commonly exceed 50 rows.
 *
 * WHY THIS IS FORM-LISTS-SPECIFIC
 *  • The shared Spreadsheet engine should stay domain-free and conservative.
 *  • SmartLists and future spreadsheet consumers should not automatically
 *    inherit a larger default row count just because Form Lists needs it.
 *  • Form Lists owns the product/workspace expectation for how many option
 *    rows should be visible before a user clicks "Add more rows".
 *
 * IMPORTANT
 *  • This is a visual workspace baseline only.
 *  • Blank padded rows are not persisted.
 *  • Save Draft / Publish still receive only meaningful rows from the
 *    shared spreadsheet snapshot/apply pipeline.
 *  • If future datasets become much larger, row virtualization should be
 *    introduced in the shared Spreadsheet engine without changing this
 *    Form Lists adapter-level configuration.
 */
export const FORM_LISTS_VISIBLE_ROW_COUNT = 75;
export const FORM_LISTS_ROW_APPEND_COUNT = DEFAULT_ROW_APPEND_COUNT;

export const FORM_LISTS_GENERATED_ROW_PREFIX = 'form-list-row';

/**
 * FORM_LISTS_COLUMN_CONFIG
 * -----------------------------------------------------------------------------
 * Shared spreadsheet engine columns for the Form Lists adapter.
 *
 * Schema alignment:
 *  • label      -> persisted dropdown option label
 *  • hint       -> persisted dropdown option hint
 *  • dependency -> custom UI cell only; dependencies are persisted separately
 *                  by DropdownDependenciesContext using optionsSetUFI/optionId
 *
 * IMPORTANT:
 *  • The visible column header is "Option", but the data key is "label"
 *    because your saved dropdown option schema uses `label`.
 */
export const FORM_LISTS_COLUMN_CONFIG = Object.freeze([
  {
    key: 'label',
    label: 'Option',
    width: 110,
    minWidth: 60,
    maxWidth: 420,
    type: 'text',
    defaultValue: '',
    persist: true,

    // Option is pasteable and meaningful. A row without an option label
    // should not be persisted as a dropdown option.
    excludeFromPaste: false,
    excludeFromMeaningfulCheck: false,

    resizable: true,
    autoFit: true,

    /**
     * Sorting
     * -------------------------------------------------------------------------
     * Option sorting is enabled for Form Lists because it represents the
     * dropdown label users expect to organize A-Z / Z-A.
     *
     * IMPORTANT
     *  • Sorting is handled by the shared Spreadsheet engine.
     *  • Sorting reorders whole option rows and preserves optionId.
     *  • Dependencies remain safe because they key by optionId, not row index.
     */
    sortable: true,

    normalizeCommittedValue: (value) => {
      if (value == null) return '';
      return String(value);
    },

    normalizePastedValue: (value) => {
      if (value == null) return '';
      return String(value);
    },
  },
  {
    key: 'hint',
    label: 'Hint',
    width: 100,
    minWidth: 60,
    maxWidth: 420,
    type: 'text',
    defaultValue: '',
    persist: true,

    // Hint is editable and pasteable, but a hint-only row should not become
    // a saved dropdown option. This matches the current Lists behavior, where
    // save includes rows only when the option label is meaningful.
    excludeFromPaste: false,
    excludeFromMeaningfulCheck: true,

    resizable: true,
    autoFit: true,

    /**
     * Sorting
     * -------------------------------------------------------------------------
     * Hint sorting is enabled for users who organize option guidance or notes.
     *
     * IMPORTANT
     *  • Hint-only rows still do not persist as dropdown options.
     *  • Empty hint values sort to the bottom in both directions.
     */
    sortable: true,

    normalizeCommittedValue: (value) => {
      if (value == null) return '';
      return String(value);
    },

    normalizePastedValue: (value) => {
      if (value == null) return '';
      return String(value);
    },
  },
  {
    key: 'dependency',
    label: 'Dep',

    /**
     * Header tooltip
     * -------------------------------------------------------------------------
     * Keep the visible spreadsheet column label compact while giving users the
     * full meaning on hover.
     *
     * IMPORTANT
     *  • The shared Spreadsheet header renderer reads this generically.
     *  • This does not change the persisted dependency schema.
     *  • This does not affect dependency icon hover behavior.
     */
    headerTooltip: 'Dependency: click icon to set dependency',

    width: 54,
    minWidth: 48,
    maxWidth: 60,
    type: 'custom',
    defaultValue: null,

    // Dependency is not a text-editable spreadsheet value.
    readOnly: true,
    editable: false,

    // Dependencies are stored separately in DropdownDependenciesContext.
    persist: false,

    // Paste should never write into dependency cells.
    excludeFromPaste: true,

    // Dependency icon state should not make a row meaningful.
    excludeFromMeaningfulCheck: true,

    // Keep dependency column compact and consistent.
    resizable: false,
    autoFit: false,

    // Dependency state is stored outside the row value model and should not be
    // sorted as a text/value column.
    sortable: false,
  },
]);

/**
 * Persisted option keys for the existing Form Lists dropdown schema.
 *
 * Do not add SmartLists lifecycle fields here. Form Lists currently persists:
 *  • optionId
 *  • label
 *  • value
 *  • hint
 *  • dep
 *  • order
 *  • systemValue
 *
 * ENTERPRISE:
 * systemValue is approved hidden option metadata for system-backed dropdowns
 * such as Asset Type. It is not a spreadsheet column and is not user-editable
 * in Form Lists.
 */
export const FORM_LISTS_PERSISTED_OPTION_KEYS = Object.freeze([
  'optionId',
  'label',
  'value',
  'hint',
  'dep',
  'order',
  'systemValue',
]);

export const FORM_LISTS_EDITABLE_CELL_KEYS = Object.freeze([
  'label',
  'hint',
]);

export const FORM_LISTS_CUSTOM_CELL_KEYS = Object.freeze([
  'dependency',
]);

export const buildFormListsDefaultColumnWidths = () => (
  buildDefaultColumnWidths(FORM_LISTS_COLUMN_CONFIG)
);

export const getFormListsColumnByKey = (columnKey) => {
  return FORM_LISTS_COLUMN_CONFIG.find(
    (column) => column.key === columnKey,
  ) || null;
};

export const isFormListsEditableColumnKey = (columnKey) => {
  return FORM_LISTS_EDITABLE_CELL_KEYS.includes(columnKey);
};

export const isFormListsCustomColumnKey = (columnKey) => {
  return FORM_LISTS_CUSTOM_CELL_KEYS.includes(columnKey);
};

export const isFormListsDependencyColumnKey = (columnKey) => {
  return columnKey === 'dependency';
};

export const createFormListsBlankOptionRow = ({
  rowId = '',
  order = 0,
} = {}) => ({
  rowId,
  optionId: '',
  label: '',
  value: '',
  hint: '',
  dep: '',
  order,
  dependency: null,
});

/**
 * Grid defaults passed into GroupedSpreadsheetCanvas.
 *
 * These are adapter-level defaults only. Generic sizing such as header height
 * and row height remains owned by the shared Spreadsheet/ engine unless Form
 * Lists later needs a real override.
 */
export const buildFormListsGridDefaults = () => ({
  columns: FORM_LISTS_COLUMN_CONFIG,
  groupIdKey: FORM_LISTS_GROUP_ID_KEY,
  rowIdKey: FORM_LISTS_ROW_ID_KEY,
  rowKey: FORM_LISTS_ROW_KEY,
  visibleRowCount: FORM_LISTS_VISIBLE_ROW_COUNT,
  rowAppendCount: FORM_LISTS_ROW_APPEND_COUNT,
  generatedRowPrefix: FORM_LISTS_GENERATED_ROW_PREFIX,
  columnWidths: buildFormListsDefaultColumnWidths(),
});